/**
 * Represents the different types of devices a user might be using
 */
export type DeviceType = 'desktop' | 'tablet' | 'mobile';

/**
 * Detects the type of device being used based on screen size and user agent
 * @returns The detected device type
 */
export const detectDevice = (): DeviceType => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'desktop'; // Default to desktop for SSR
  }

  // First try to detect mobile/tablet using user agent
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileByAgent = /iphone|ipad|ipod|android|blackberry|windows phone/i.test(userAgent);
  const isTabletByAgent = /ipad|android(?!.*mobile)/i.test(userAgent);

  // Get screen dimensions
  const width = window.innerWidth;
  const height = window.innerHeight;
  const screenRatio = width / height;

  // Define breakpoints
  const MOBILE_MAX_WIDTH = 768;
  const TABLET_MAX_WIDTH = 1024;

  // If device is detected as mobile/tablet by user agent, use that
  if (isTabletByAgent) return 'tablet';
  if (isMobileByAgent) return 'mobile';

  // Otherwise fallback to screen size detection
  if (width <= MOBILE_MAX_WIDTH) return 'mobile';
  if (width <= TABLET_MAX_WIDTH && screenRatio < 1.3) return 'tablet';

  return 'desktop';
}

/**
 * Checks if the current device is a mobile device
 * @returns True if the device is a mobile phone
 */
export const isMobile = (): boolean => {
  return detectDevice() === 'mobile';
}

/**
 * Checks if the current device is a tablet
 * @returns True if the device is a tablet
 */
export const isTablet = (): boolean => {
  return detectDevice() === 'tablet';
}

/**
 * Checks if the current device is a desktop computer
 * @returns True if the device is a desktop
 */
export const isDesktop = (): boolean => {
  return detectDevice() === 'desktop';
}
